/**
 * Centralized Query & Mutation Definitions using React Query Kit
 *
 * This file serves as the single source of truth for all API queries and mutations
 * across the application. It uses react-query-kit's router pattern for:
 * - Type-safe query keys
 * - Reusable hook generation
 * - Centralized cache invalidation
 * - Middleware support for cross-cutting concerns
 *
 * Benefits:
 * ✅ No more manual queryKey construction
 * ✅ Type-safe key access: api.schools.report.getKey(variables)
 * ✅ Easy prefetching: queryClient.prefetchQuery(api.schools.report.getFetchOptions(...))
 * ✅ Centralized invalidation: queryClient.invalidateQueries(api.schools.getKey())
 * ✅ Middleware for logging, error tracking, caching strategies
 *
 * Usage:
 * ```tsx
 * import { api } from '@/core/api/queries';
 *
 * function SchoolsReportPage() {
 *   const report = api.schools.report.useQuery({
 *     variables: { filter, pageable }
 *   });
 *
 *   const updateMutation = api.schools.update.useMutation({
 *     onSuccess: () => {
 *       queryClient.invalidateQueries(api.schools.report.getKey());
 *     }
 *   });
 * }
 * ```
 */

import { router } from 'react-query-kit';

// API Imports
import { schoolsApi } from '../../domains/schools/api/schools.api';
import { usersApi } from '../../domains/users/api/users.api';
import { wasteCollectionApi } from '../../domains/waste-collection/api/waste-collection.api';
import { notificationsApi, type NotificationReportDto } from '../../domains/notifications/api/notifications.api';
import { schoolRequestsApi, type SchoolRequestReportDto } from '../../domains/school-requests/api/school-requests.api';
import { lessonsAPI } from '../../domains/lessons/api';
import { studentsApi } from '../../domains/students/api/students.api';
import { editionsApi } from '../../shared/api/editions.api';
import type { Pageable } from './spring-types';
import type {
  SchoolDto,
  SchoolReportDto,
  EditionReportDto,
  UserDto,
  WasteCollectionFormDto,
  WasteCollectionFormReportDto,
  EditionDto,
  LessonAnswerReportDto
} from '../../api/generated/types.gen';
import { ticketsApi, type TicketRedemptionStatus } from '../../domains/tickets/api/tickets.api';
import {
  wasteContainerRequestsApi,
  type FiltruRaportRecipiente,
} from '../../domains/recipients/api/waste-container-requests.api';

/**
 * Middleware: Report Query Logger
 * Logs all report queries for debugging and monitoring
 */
const reportQueryLogger = (useQueryNext: any) => {
  return (options: any) => {
    // Debug logging removed - uncomment if needed for debugging
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('[ReportQuery]', {
    //     key: options.queryKey,
    //     timestamp: new Date().toISOString(),
    //   });
    // }
    return useQueryNext(options);
  };
};

/**
 * Centralized API Definition using Router Pattern
 *
 * Structure:
 * - api.schools.report → School report queries
 * - api.schools.update → School mutation
 * - api.users.report → User report queries
 * - api.wasteOrders.report → Waste order queries
 * - etc.
 */
export const api = {
  /**
   * Schools Domain
   * Handles school listing, reports, CRUD, and dropdowns
   */
  schools: router('schools', {
    /**
     * School Report Query
     * Paginated, filterable list of schools
     */
    report: router.query({
      fetcher: ({ filter, pageable }: { filter: Partial<SchoolReportDto>; pageable: Pageable }) =>
        schoolsApi.getReport(filter as any, pageable),
      staleTime: 5 * 60 * 1000, // 5 minutes
      use: [reportQueryLogger],
    }),

    /**
     * School Status Report Query
     * Edition-aware status report for schools
     */
    statusReport: router.query({
      fetcher: ({ filter, pageable }: { filter: Partial<EditionReportDto>; pageable: Pageable }) =>
        schoolsApi.getStatusReport(filter as any, pageable),
      staleTime: 5 * 60 * 1000,
      use: [reportQueryLogger],
    }),

    /**
     * Import Data Query
     * School data import preparation
     */
    importData: router.query({
      fetcher: ({ filter, pageable }: { filter: any; pageable: Pageable }) =>
        schoolsApi.getImportData(filter, pageable),
      staleTime: 10 * 60 * 1000,
    }),


    /**
     * Get School by ID Query
     * Single school details
     */
    byId: router.query({
      fetcher: ({ id }: { id: string }) => schoolsApi.getById(id),
      staleTime: 5 * 60 * 1000,
    }),

    /**
     * Get Own School Query
     * Current user's school (if professor)
     */
    own: router.query({
      fetcher: () => schoolsApi.getOwn(),
      staleTime: 10 * 60 * 1000,
    }),

    /**
     * Get Default School Query
     * Template/default school for new entries
     */
    default: router.query({
      fetcher: () => schoolsApi.getDefault(),
      staleTime: 60 * 60 * 1000, // 1 hour (rarely changes)
    }),

    /**
     * Dropdown Schools Query
     * For select inputs
     */
    dropdown: router.query({
      fetcher: () => schoolsApi.getDropdown(),
      staleTime: 30 * 60 * 1000,
    }),

    /**
     * Counties Query
     * Static reference data
     */
    counties: router.query({
      fetcher: () => schoolsApi.getCounties(),
      staleTime: 24 * 60 * 60 * 1000, // 24 hours (never changes)
    }),

    /**
     * Update School Mutation
     */
    update: router.mutation({
      mutationFn: (data: SchoolDto) => schoolsApi.update(data as any),
    }),

    /**
     * Create School Mutation
     */
    create: router.mutation({
      mutationFn: (data: SchoolDto) => schoolsApi.create(data as any),
    }),

    /**
     * Delete School Mutation
     */
    delete: router.mutation({
      mutationFn: (id: string) => schoolsApi.delete(id),
    }),
  }),

  /**
   * Users Domain
   * Handles user listing, reports, and profile operations
   */
  users: router('users', {
    /**
     * Users Report Query
     * Paginated, filterable list of users
     */
    report: router.query({
      fetcher: ({ filter, pageable }: { filter: Partial<UserDto>; pageable: Pageable }) =>
        usersApi.getReport(filter as any, pageable),
      staleTime: 5 * 60 * 1000,
      use: [reportQueryLogger],
    }),

    /**
     * Get User Profile Query
     * Current user's profile
     */
    profile: router.query({
      fetcher: () => usersApi.getProfile(),
      staleTime: 10 * 60 * 1000,
    }),

    /**
     * Update User Activation Mutation
     * Toggle user active/inactive status
     */
    updateActivation: router.mutation({
      mutationFn: (data: Partial<UserDto>) => usersApi.updateActivation(data),
    }),

    /**
     * Update Phone Call Note Mutation
     */
    updatePhoneNote: router.mutation({
      mutationFn: ({ userId, note }: { userId: string; note: string }) =>
        usersApi.updatePhoneNote(userId, note),
    }),

    /**
     * Update User Signature Mutation
     * Accepts full UserSignatureDto with signature + all boolean consents
     */
    updateSignature: router.mutation({
      mutationFn: (data: any) => usersApi.updateSignature(data),
    }),

    /**
     * Update User Profile Mutation
     */
    updateProfile: router.mutation({
      mutationFn: (data: Partial<UserDto>) => usersApi.updateProfile(data),
    }),
  }),

  /**
   * Waste Collection Domain
   * Handles waste collection forms, reports, and operations
   */
  wasteOrders: router('waste-orders', {
    /**
     * Waste Orders Report Query
     * Paginated, filterable list of waste orders
     */
    report: router.query({
      fetcher: ({ filter, pageable }: { filter: Partial<WasteCollectionFormReportDto>; pageable: Pageable }) =>
        wasteCollectionApi.getReport(filter, pageable),
      staleTime: 5 * 60 * 1000,
      use: [reportQueryLogger],
    }),

    /**
     * Get Waste Order by ID Query
     */
    byId: router.query({
      fetcher: ({ id }: { id: string }) => wasteCollectionApi.getById(id),
      staleTime: 5 * 60 * 1000,
    }),

    /**
     * Get All Waste Orders Query (no pagination)
     */
    all: router.query({
      fetcher: () => wasteCollectionApi.getAll(),
      staleTime: 10 * 60 * 1000,
    }),

    /**
     * Get Default Waste Form Template Query
     */
    default: router.query({
      fetcher: () => wasteCollectionApi.getDefault(),
      staleTime: 60 * 60 * 1000,
    }),

    /**
     * Create Waste Order Mutation
     */
    create: router.mutation({
      mutationFn: (data: WasteCollectionFormDto) => wasteCollectionApi.create(data),
    }),

    /**
     * Update Waste Order Mutation
     */
    update: router.mutation({
      mutationFn: (data: WasteCollectionFormDto) => wasteCollectionApi.update(data),
    }),

    /**
     * Admin Update Waste Order Mutation
     */
    adminUpdate: router.mutation({
      mutationFn: (data: WasteCollectionFormDto) => wasteCollectionApi.adminUpdate(data),
    }),

    /**
     * Update Waste Order Validation Status Mutation
     * (approve/reject with reason)
     */
    updateValidation: router.mutation({
      mutationFn: ({ id, ...params }: { id: string; isValidated?: boolean; rejectReason?: string }) =>
        wasteCollectionApi.updateValidation(id, params),
    }),

    /**
     * Update Logistics Data Mutation
     * (real waste values and documents)
     */
    updateLogistics: router.mutation({
      mutationFn: ({ id, data }: { id: string; data: any }) =>
        wasteCollectionApi.updateLogistics(id, data),
    }),

    /**
     * Mark Order as Processed Mutation
     */
    markProcessed: router.mutation({
      mutationFn: (id: string) => wasteCollectionApi.markProcessed(id),
    }),

    /**
     * Delete Waste Order Mutation
     */
    delete: router.mutation({
      mutationFn: (id: string) => wasteCollectionApi.delete(id),
    }),
  }),

  /**
   * Notifications Domain
   * Handles notification reports and operations
   */
  notifications: router('notifications', {
    /**
     * Notifications Report Query
     * Paginated, filterable list of notifications
     */
    report: router.query({
      fetcher: ({ filter, pageable }: { filter: Partial<NotificationReportDto>; pageable: Pageable }) =>
        notificationsApi.getReport(filter, pageable),
      staleTime: 5 * 60 * 1000,
      use: [reportQueryLogger],
    }),
  }),

  /**
   * School Requests Domain
   * Handles school registration requests and approvals
   */
  schoolRequests: router('school-requests', {
    /**
     * School Requests Report Query
     * Paginated, filterable list of school requests
     */
    report: router.query({
      fetcher: ({ filter, pageable }: { filter: Partial<SchoolRequestReportDto>; pageable: Pageable }) =>
        schoolRequestsApi.getReport(filter, pageable),
      staleTime: 5 * 60 * 1000,
      use: [reportQueryLogger],
    }),
  }),

  /**
   * Lessons Domain
   * Handles lessons, quizzes, challenges, and student answer reports
   */
  lessons: router('lessons', {
    /**
     * Lesson Answers Report Query
     * Paginated, filterable list of student answers to lessons/quizzes
     */
    answers: {
      report: router.query({
        fetcher: ({ filter, pageable }: { filter: Partial<LessonAnswerReportDto>; pageable: Pageable }) =>
          lessonsAPI.getAnswersReport(filter, pageable),
        staleTime: 2 * 60 * 1000, // 2 minutes (more dynamic than other reports)
        use: [reportQueryLogger],
      }),
    },
  }),

  /**
   * Students Domain
   * Handles the students leaderboard (clasament elevi pentru premiere)
   */
  students: router('students', {
    /**
     * Students Leaderboard Query
     * Returns the full leaderboard ordered by `total` desc (one large Page).
     * Per-column sorting & pagination are handled client-side on the page.
     */
    leaderboard: router.query({
      fetcher: ({ filter, pageable }: { filter: { editionId?: string }; pageable: Pageable }) =>
        studentsApi.getLeaderboard(filter, pageable),
      staleTime: 5 * 60 * 1000,
      use: [reportQueryLogger],
    }),
  }),

  /**
   * Editions Domain
   * Handles program editions (years/cycles)
   */
  /**
   * Tickets Domain (spec 2026-08-28 §8)
   * Soldul scolii, nivelurile per editie, rascumpararile.
   *
   * staleTime-urile de mai jos sunt INTENTIE, nu mecanism: clientul global le anuleaza
   * (staleTime: 0 - capcana 4.4 din outstanding-issues.md). Le scriem ca sa ramana citibil ce
   * anume consideram volatil, daca cineva repara vreodata configul global.
   */
  tickets: router('tickets', {
    tiers: router.query({
      fetcher: ({ editionId }: { editionId?: string }) => ticketsApi.getTiers(editionId),
      staleTime: 5 * 60 * 1000,
    }),
    ownBalance: router.query({
      fetcher: () => ticketsApi.getOwnBalance(),
      staleTime: 0, // se schimba la fiecare rascumparare si la fiecare anulare facuta de ECOTIC
    }),
    ownRedemptions: router.query({
      fetcher: () => ticketsApi.getOwnRedemptions(),
      staleTime: 0,
    }),
    redemptions: router.query({
      fetcher: ({ filter, pageable }: { filter: { editionId?: string; status?: TicketRedemptionStatus }; pageable: Pageable }) =>
        ticketsApi.getRedemptions(filter, pageable),
      staleTime: 60 * 1000,
      use: [reportQueryLogger],
    }),
  }),

  /**
   * Cererile de recipiente (SP-04).
   *
   * `own` are `staleTime: 0` INTENTIONAT: e chiar sursa starii de pe ecranul
   * profesorului, iar dupa o cerere reusita ecranul trebuie sa arate adevarul de
   * la server, nu ce credea inainte de apasare. (Clientul global anuleaza oricum
   * staleTime-urile — capcana 4.4 — dar intentia ramane citibila.)
   */
  wasteContainerRequests: router('wasteContainerRequests', {
    own: router.query({
      fetcher: () => wasteContainerRequestsApi.getOwn(),
      staleTime: 0,
    }),
    report: router.query({
      fetcher: ({ filter, pageable }: { filter: FiltruRaportRecipiente; pageable: Pageable }) =>
        wasteContainerRequestsApi.getReport(filter, pageable),
      staleTime: 60 * 1000,
      use: [reportQueryLogger],
    }),
    contoare: router.query({
      fetcher: ({ editionId }: { editionId?: string }) => wasteContainerRequestsApi.getContoare(editionId),
      staleTime: 60 * 1000,
    }),
  }),

  editions: router('editions', {
    /**
     * Get All Editions Query
     */
    all: router.query({
      fetcher: () => editionsApi.getAll(),
      staleTime: 60 * 60 * 1000, // 1 hour
    }),

    /**
     * Get Edition by ID Query
     */
    byId: router.query({
      fetcher: ({ id }: { id: string }) => editionsApi.getById(id),
      staleTime: 60 * 60 * 1000,
    }),

    /**
     * Linkurile documentelor editiei curente (SE-14) — publica, functioneaza si fara login.
     * Fara parametri: backendul le rezolva pe editia curenta, plus fallback intern pe valorile
     * de azi ale `DOCUMENT_LINKS` (deci raspunsul are mereu patru chei nenule).
     */
    documents: router.query({
      fetcher: () => editionsApi.getDocuments(),
      staleTime: 60 * 60 * 1000, // 1 ora -- linkuri de document, rar schimbate
    }),

    /**
     * Create Edition Mutation (admin only)
     */
    create: router.mutation({
      mutationFn: (data: EditionDto) => editionsApi.create(data),
    }),

    /**
     * Update Edition Mutation (admin only)
     */
    update: router.mutation({
      mutationFn: (data: EditionDto) => editionsApi.update(data),
    }),

    /**
     * Delete Edition Mutation (admin only)
     */
    delete: router.mutation({
      mutationFn: (id: string) => editionsApi.delete(id),
    }),
  }),
};

/**
 * Type Inference Utilities
 * Use these to extract types from query/mutation definitions
 *
 * Example:
 * type SchoolReportData = inferData<typeof api.schools.report>;
 * type SchoolReportVariables = inferVariables<typeof api.schools.report>;
 */
export type { inferData, inferVariables, inferError } from 'react-query-kit';
